import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import api from '@/utils/api';
import { getUser, setUser, removeToken } from '@/utils/auth';
import { toast } from 'sonner';
import { ArrowLeft, Camera, User, Mail, Phone, FileText, Save, Loader2, MapPin, Trash2, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

const StudentProfilePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const user = getUser();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    avatar_url: null,
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postal_code: '',
    shipping_country: ''
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        avatar_url: user.avatar_url || null,
        shipping_address: user.shipping_address || '',
        shipping_city: user.shipping_city || '',
        shipping_state: user.shipping_state || '',
        shipping_postal_code: user.shipping_postal_code || '',
        shipping_country: user.shipping_country || ''
      });
    }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const response = await api.get('/auth/me');
      const profileData = {
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        bio: response.data.bio || '',
        avatar_url: response.data.avatar_url || null,
        shipping_address: response.data.shipping_address || '',
        shipping_city: response.data.shipping_city || '',
        shipping_state: response.data.shipping_state || '',
        shipping_postal_code: response.data.shipping_postal_code || '',
        shipping_country: response.data.shipping_country || ''
      };
      setProfile(profileData);
      
      // Sync localStorage with fresh data from API
      const currentUser = getUser();
      if (currentUser) {
        setUser({ ...currentUser, ...response.data });
      }
    } catch (error) {
      // Use cached user data
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const response = await api.put('/auth/profile', {
        name: profile.name,
        phone: profile.phone,
        bio: profile.bio,
        shipping_address: profile.shipping_address,
        shipping_city: profile.shipping_city,
        shipping_state: profile.shipping_state,
        shipping_postal_code: profile.shipping_postal_code,
        shipping_country: profile.shipping_country
      });
      
      // Update local storage
      const updatedUser = { ...user, ...response.data.user };
      setUser(updatedUser);
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/auth/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update profile with new avatar URL (add cache buster)
      const avatarUrlWithCacheBuster = `${response.data.avatar_url}${response.data.avatar_url.includes('?') ? '&' : '?'}v=${Date.now()}`;
      setProfile(prev => ({ ...prev, avatar_url: avatarUrlWithCacheBuster }));
      
      // Update local storage
      const updatedUser = { ...user, avatar_url: avatarUrlWithCacheBuster };
      setUser(updatedUser);
      
      // Refresh profile to ensure consistency
      await fetchProfile();
      
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const getAvatarUrl = () => {
    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      return `${process.env.REACT_APP_BACKEND_URL || ''}${profile.avatar_url}`;
    }
    return null;
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete('/auth/delete-account');
      toast.success('Your account has been deleted');
      removeToken();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-6" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/student/dashboard')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <Card style={{ borderColor: '#E3E1DB' }}>
            <CardHeader>
              <CardTitle 
                className="text-2xl"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                My Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-6">
                {/* Avatar Section */}
                <div className="flex flex-col items-center mb-6">
                  <div 
                    className="relative cursor-pointer group"
                    onClick={handleAvatarClick}
                  >
                    <div 
                      className="w-32 h-32 rounded-full overflow-hidden border-4 flex items-center justify-center"
                      style={{ 
                        borderColor: '#163329',
                        backgroundColor: '#F0EFEA'
                      }}
                    >
                      {(isLoadingProfile || isUploadingAvatar) ? (
                        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
                      ) : getAvatarUrl() ? (
                        <img 
                          src={getAvatarUrl()} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="h-16 w-16" style={{ color: '#5C635A' }} />
                      )}
                    </div>
                    <div 
                      className="absolute bottom-0 right-0 p-2 rounded-full transition-colors"
                      style={{ backgroundColor: '#163329' }}
                    >
                      <Camera className="h-4 w-4 text-white" />
                    </div>
                    <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <p className="text-sm mt-2" style={{ color: '#5C635A' }}>
                    Click to upload a profile photo
                  </p>
                </div>

                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4" style={{ color: '#5C635A' }} />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your full name"
                    data-testid="profile-name-input"
                  />
                </div>

                {/* Email Field (Read-only) */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" style={{ color: '#5C635A' }} />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    value={profile.email}
                    disabled
                    className="bg-gray-50"
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs" style={{ color: '#5C635A' }}>
                    Email cannot be changed
                  </p>
                </div>

                {/* Phone Field */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" style={{ color: '#5C635A' }} />
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Enter your phone number"
                    data-testid="profile-phone-input"
                  />
                </div>

                {/* Bio Field */}
                <div className="space-y-2">
                  <Label htmlFor="bio" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" style={{ color: '#5C635A' }} />
                    About Me
                  </Label>
                  <Textarea
                    id="bio"
                    value={profile.bio}
                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us a bit about yourself..."
                    rows={4}
                    data-testid="profile-bio-input"
                  />
                </div>

                {/* Shipping Address Section */}
                <div className="pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <MapPin className="h-5 w-5" />
                    Shipping Address
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                    Required for courses that include learning kits. Your materials will be shipped to this address.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Street Address */}
                    <div className="space-y-2">
                      <Label htmlFor="shipping_address">Street Address</Label>
                      <Input
                        id="shipping_address"
                        value={profile.shipping_address}
                        onChange={(e) => setProfile(prev => ({ ...prev, shipping_address: e.target.value }))}
                        placeholder="123 Main Street, Apt 4B"
                        data-testid="shipping-address-input"
                      />
                    </div>
                    
                    {/* City and State */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="shipping_city">City</Label>
                        <Input
                          id="shipping_city"
                          value={profile.shipping_city}
                          onChange={(e) => setProfile(prev => ({ ...prev, shipping_city: e.target.value }))}
                          placeholder="City"
                          data-testid="shipping-city-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shipping_state">State/Province</Label>
                        <Input
                          id="shipping_state"
                          value={profile.shipping_state}
                          onChange={(e) => setProfile(prev => ({ ...prev, shipping_state: e.target.value }))}
                          placeholder="State"
                          data-testid="shipping-state-input"
                        />
                      </div>
                    </div>
                    
                    {/* Postal Code and Country */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="shipping_postal_code">Postal Code</Label>
                        <Input
                          id="shipping_postal_code"
                          value={profile.shipping_postal_code}
                          onChange={(e) => setProfile(prev => ({ ...prev, shipping_postal_code: e.target.value }))}
                          placeholder="12345"
                          data-testid="shipping-postal-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shipping_country">Country</Label>
                        <Input
                          id="shipping_country"
                          value={profile.shipping_country}
                          onChange={(e) => setProfile(prev => ({ ...prev, shipping_country: e.target.value }))}
                          placeholder="Country"
                          data-testid="shipping-country-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="w-full"
                  
                  data-testid="save-profile-button"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Profile
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Delete Account Section */}
          <Card className="mt-6 border-red-200" style={{ backgroundColor: '#FEF2F2' }}>
            <CardHeader>
              <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-red-600 mb-4">
                Once you delete your account, there is no going back. This will permanently delete your account, 
                course enrollments, and all associated data.
              </p>
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteDialog(true)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="delete-account-button"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete My Account
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-red-600 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Delete Account
                    </DialogTitle>
                    <DialogDescription>
                      This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <Label htmlFor="confirm-delete" className="text-sm font-medium">
                      Type <span className="font-bold text-red-600">DELETE</span> to confirm:
                    </Label>
                    <Input
                      id="confirm-delete"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className="mt-2"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteDialog(false);
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="confirm-delete-button"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Account
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default StudentProfilePage;
